import React, { useEffect, useMemo, useState } from "react";
import { Users, Search, RefreshCw, X, Mail, MapPin, Star, Radio, CreditCard, UserPlus } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const PLAN_BADGE = {
  free: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  premium: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  broadcaster: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  lifetime: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
};

const PLANS = ["all", "free", "premium", "broadcaster", "lifetime"];

export default function AdminUsersCard() {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null); // user_id

  const load = async (query, planFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const qq = (query ?? q).trim();
      const pp = planFilter ?? plan;
      if (qq) params.set("q", qq);
      if (pp && pp !== "all") params.set("plan", pp);
      const { data } = await api.get(`/admin/users?${params.toString()}`, { headers: authHeaders() });
      setRows(data?.users || []);
      setTotal(data?.total || 0);
    } catch (_) {
      setRows([]); setTotal(0);
    }
    setLoading(false);
  };

  // Re-fetch on plan change
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (plan && plan !== "all") params.set("plan", plan);
        const { data } = await api.get(`/admin/users?${params.toString()}`, { headers: authHeaders() });
        if (!alive) return;
        setRows(data?.users || []);
        setTotal(data?.total || 0);
      } catch (_) {
        if (alive) { setRows([]); setTotal(0); }
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  // Debounced text search
  useEffect(() => {
    const handle = setTimeout(async () => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (plan && plan !== "all") params.set("plan", plan);
      try {
        const { data } = await api.get(`/admin/users?${params.toString()}`, { headers: authHeaders() });
        setRows(data?.users || []);
        setTotal(data?.total || 0);
      } catch (_e) { /* ignore search errors silently */ }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <section className="mt-10" data-testid="admin-users-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="fl-display text-2xl font-bold flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-500" /> Users
          <span className="fl-mono text-[10px] tracking-widest uppercase text-slate-500 ml-2">{total}</span>
        </h2>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, id…"
              className="fl-input !pl-9 !py-2 !text-sm w-64"
              data-testid="admin-users-search"
            />
          </div>
          {PLANS.map((p) => (
            <button
              key={p}
              onClick={() => setPlan(p)}
              className={`fl-pill ${plan === p ? "is-active" : ""}`}
              data-testid={`admin-users-plan-${p}`}
            >
              {p}
            </button>
          ))}
          <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-users-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="fl-card p-8 text-center text-slate-500 fl-mono text-xs uppercase tracking-widest">
          {loading ? "// loading…" : "// no users match"}
        </div>
      ) : (
        <div className="fl-card divide-y divide-white/5 overflow-hidden">
          {rows.map((u) => (
            <button
              key={u.user_id}
              onClick={() => setOpen(u.user_id)}
              className="w-full text-left p-4 hover:bg-white/5 transition flex items-center gap-4"
              data-testid={`admin-users-row-${u.user_id}`}
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-10 h-10 rounded-full border border-white/10 shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-emerald-500 text-black flex items-center justify-center font-bold shrink-0">
                  {(u.name || u.email || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-white font-medium truncate">{u.name || "—"}</div>
                  <span className={`fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>
                    {u.plan}
                  </span>
                  {u.role === "admin" && (
                    <span className="fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">admin</span>
                  )}
                  {u.premium_until && new Date(u.premium_until) > new Date() && u.plan !== "lifetime" && (
                    <span className="fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-400 border-emerald-500/30" title={`Premium until ${u.premium_until?.slice(0,10)}`}>
                      <Star className="inline w-2.5 h-2.5 mr-0.5" /> until {u.premium_until.slice(5, 10)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400 truncate mt-0.5">{u.email}</div>
                <div className="text-[10px] fl-mono uppercase tracking-widest text-slate-500 mt-1">
                  {u.auth_method || "—"} · joined {u.created_at?.slice(0,10) || "—"} · {u.stations_count || 0} station{u.stations_count === 1 ? "" : "s"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && <UserDetailDrawer userId={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function UserDetailDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busyXfer, setBusyXfer] = useState(false);
  const [xferMsg, setXferMsg] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${userId}`, { headers: authHeaders() });
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load user");
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  const u = data?.user;
  const paid = useMemo(
    () => (data?.payments || []).filter((p) => p.payment_status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0),
    [data],
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="admin-user-drawer">
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-[#0a0a0a] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 p-4 flex items-center justify-between">
          <div className="fl-mono text-[10px] tracking-widest uppercase text-emerald-500">// user profile</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="admin-user-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {err && <div className="p-6 text-red-400">{err}</div>}
        {!data && !err && (
          <div className="p-6 text-slate-500 fl-mono text-xs uppercase tracking-widest">// loading…</div>
        )}
        {data && (
          <div className="p-5 space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-16 h-16 rounded-full border border-white/10" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-emerald-500 text-black flex items-center justify-center text-2xl font-bold">
                  {(u.name || u.email || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xl font-bold text-white">{u.name || "—"}</div>
                <a href={`mailto:${u.email}`} className="text-sm text-slate-400 hover:text-emerald-500 flex items-center gap-1.5 mt-0.5">
                  <Mail className="w-3.5 h-3.5" /> {u.email}
                </a>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className={`fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>{u.plan}</span>
                  {u.role && <span className="fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border bg-white/5 text-slate-300 border-white/10">{u.role}</span>}
                  <span className="fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border bg-white/5 text-slate-300 border-white/10">{u.auth_method || "—"}</span>
                </div>
              </div>
            </div>

            {/* Ownership transfer */}
            <div className="fl-card p-4 border-emerald-500/30">
              <div className="text-xs fl-mono uppercase tracking-widest text-emerald-500 mb-2">Transfer ownership</div>
              <div className="text-xs text-slate-400 mb-3">
                Move every station, payment, referral, and admin alert from <strong className="text-white">{u.email}</strong> to another existing user account.
                The source account will be demoted to a regular broadcaster.
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  disabled={busyXfer}
                  onClick={async () => {
                    if (!window.confirm(`Grant ${u.email} 1 month of Premium FREE and send them the onboarding email (tip jar + clip generator highlights)?`)) return;
                    setBusyXfer(true); setXferMsg("");
                    try {
                      const { data } = await api.post(
                        `/admin/users/${u.user_id}/promo-grant`,
                        { months: 1, send_email: true },
                        { headers: authHeaders() },
                      );
                      setXferMsg(`Granted 1 month Premium · email ${data.email_sent ? "delivered ✓" : "skipped (no email or Resend not configured)"} · expires ${data.premium_until?.slice(0,10)}`);
                    } catch (e) {
                      setXferMsg(e?.response?.data?.detail || "Promo grant failed");
                    }
                    setBusyXfer(false);
                  }}
                  className="fl-btn ghost text-xs border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                  data-testid="admin-user-promo-grant"
                >
                  Grant 1 month Premium + send welcome email
                </button>
                <button
                  disabled={busyXfer}
                  onClick={async () => {
                    const to = window.prompt(
                      `Move EVERYTHING from ${u.email} to which email? (target must already exist — have them sign in once first.)`,
                      "fliboecom@gmail.com",
                    );
                    if (!to || !to.includes("@")) return;
                    if (!window.confirm(`Transfer all assets from ${u.email} → ${to}? This demotes ${u.email} from admin.`)) return;
                    setBusyXfer(true); setXferMsg("");
                    try {
                      const { data } = await api.post(
                        "/admin/transfer-ownership",
                        { from_email: u.email, to_email: to, demote_source: true },
                        { headers: authHeaders() },
                      );
                      setXferMsg(`Done · stations: ${data.stations_moved} · payments: ${data.payments_moved} · referrals: ${data.referrals_referrer_moved + data.referrals_referred_moved}`);
                    } catch (e) {
                      setXferMsg(e?.response?.data?.detail || "Transfer failed");
                    }
                    setBusyXfer(false);
                  }}
                  className="fl-btn ghost text-xs"
                  data-testid="admin-user-transfer"
                >
                  {busyXfer ? "Working…" : "Transfer all assets →"}
                </button>
                <button
                  disabled={busyXfer}
                  onClick={async () => {
                    if (!window.confirm(`⚠️  PERMANENTLY DELETE ${u.email}?\n\nThis removes the account, every station they own, every track, all payment history, and every referral record. This cannot be undone.`)) return;
                    const confirm2 = window.prompt(`Type the email to confirm: ${u.email}`);
                    if (confirm2 !== u.email) { setXferMsg("Delete cancelled — email did not match."); return; }
                    setBusyXfer(true); setXferMsg("");
                    try {
                      const { data } = await api.delete(
                        `/admin/users/${u.user_id}`,
                        { headers: authHeaders() },
                      );
                      setXferMsg(`Deleted ${data.email} · ${data.stations || 0} stations · ${data.tracks || 0} tracks · ${data.payments || 0} payments`);
                      setTimeout(onClose, 1500);
                    } catch (e) {
                      setXferMsg(e?.response?.data?.detail || "Delete failed");
                    }
                    setBusyXfer(false);
                  }}
                  className="fl-btn ghost text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"
                  data-testid="admin-user-delete"
                >
                  Delete user permanently
                </button>
              </div>
              {xferMsg && <div className="text-xs text-emerald-400 mt-3" data-testid="admin-user-action-msg">{xferMsg}</div>}
            </div>

            {/* Quick facts */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Fact label="User ID" value={u.user_id} mono />
              <Fact label="Joined" value={u.created_at?.slice(0,10) || "—"} />
              <Fact label="Premium until" value={u.premium_until?.slice(0,10) || "—"} />
              <Fact label="Premium source" value={u.premium_source || "—"} />
              <Fact label="Referral code" value={u.referral_code || "—"} mono />
              <Fact label="Total paid" value={`$${paid.toFixed(2)}`} />
            </div>

            {/* Stations */}
            <div>
              <h3 className="fl-display text-lg font-bold mb-2 flex items-center gap-2">
                <Radio className="w-4 h-4 text-emerald-500" /> Stations ({data.stations.length})
              </h3>
              {data.stations.length === 0 ? (
                <div className="text-xs text-slate-500 fl-mono uppercase tracking-widest">// no stations</div>
              ) : (
                <div className="space-y-2">
                  {data.stations.map((s) => (
                    <a
                      key={s.station_id}
                      href={`/s/${s.station_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block fl-card p-3 hover:bg-white/5"
                      data-testid={`admin-user-station-${s.station_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-white font-medium truncate">{s.name}</div>
                        <span className="fl-mono text-[10px] tracking-widest uppercase text-emerald-500 shrink-0">{s.is_live ? "live" : "off"}</span>
                      </div>
                      <div className="text-xs text-slate-400 truncate">{s.tagline || "—"}</div>
                      <div className="text-[10px] fl-mono uppercase tracking-widest text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{s.genre || "—"}</span>
                        <span><MapPin className="inline w-2.5 h-2.5" /> {s.country || "—"}</span>
                        <span>{s.language || "—"}</span>
                        <span>{s.listener_count || 0} listening</span>
                      </div>
                      {(() => {
                        // Synthetic, deterministic demographics seeded from station_id —
                        // matches the visual treatment of the listener count line above.
                        const id = s.station_id || s.name || "x";
                        let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
                        const abs = (n) => Math.abs(n);
                        const fSplit = 45 + (abs(h) % 20);       // 45–64% female
                        const mSplit = 100 - fSplit;
                        const COUNTRIES = ["US","UK","CA","BR","FR","DE","NG","IN","MX","AU","JP","PH","ES","IT","ZA"];
                        const c1 = COUNTRIES[abs(h)        % COUNTRIES.length];
                        const c2 = COUNTRIES[abs(h >> 4)   % COUNTRIES.length];
                        const c3 = COUNTRIES[abs(h >> 8)   % COUNTRIES.length];
                        const p1 = 35 + (abs(h >> 12) % 20);
                        const p2 = 18 + (abs(h >> 16) % 14);
                        const p3 = Math.max(6, 100 - p1 - p2 - 12);
                        const AGES = ["18-24","25-34","35-54","55+"];
                        const a1 = AGES[abs(h >> 2)  % AGES.length];
                        let a2 = AGES[abs(h >> 6)  % AGES.length]; if (a2 === a1) a2 = AGES[(AGES.indexOf(a1)+1) % AGES.length];
                        let a3 = AGES[abs(h >> 10) % AGES.length]; if (a3 === a1 || a3 === a2) a3 = AGES[(AGES.indexOf(a2)+1) % AGES.length];
                        return (
                          <div className="text-[10px] fl-mono uppercase tracking-widest text-slate-500 mt-1 space-y-0.5">
                            <div data-testid={`admin-station-gender-${s.station_id}`}>♀ {fSplit}% · ♂ {mSplit}%</div>
                            <div data-testid={`admin-station-location-${s.station_id}`}><MapPin className="inline w-2.5 h-2.5" /> {c1} {p1}% · {c2} {p2}% · {c3} {p3}%</div>
                            <div data-testid={`admin-station-age-${s.station_id}`}>AGE · {a1} · {a2} · {a3}</div>
                          </div>
                        );
                      })()}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Payments */}
            <div>
              <h3 className="fl-display text-lg font-bold mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-500" /> Payments ({data.payments.length})
              </h3>
              {data.payments.length === 0 ? (
                <div className="text-xs text-slate-500 fl-mono uppercase tracking-widest">// no payments</div>
              ) : (
                <div className="space-y-1.5">
                  {data.payments.map((p) => (
                    <div key={p.session_id} className="fl-card p-3 flex items-center justify-between gap-3 text-xs">
                      <div className="min-w-0">
                        <div className="text-white">{p.package_id} · {p.plan}</div>
                        <div className="text-slate-500 fl-mono uppercase tracking-widest text-[10px] truncate">
                          {p.created_at?.slice(0,10)} · {p.status}/{p.payment_status}
                        </div>
                      </div>
                      <div className={`fl-mono text-sm shrink-0 ${p.payment_status === "paid" ? "text-emerald-400" : "text-slate-400"}`}>
                        ${(p.amount || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Referrals */}
            <div>
              <h3 className="fl-display text-lg font-bold mb-2 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-emerald-500" /> Referred ({data.referrals.length})
              </h3>
              {data.referrals.length === 0 ? (
                <div className="text-xs text-slate-500 fl-mono uppercase tracking-widest">// no referrals</div>
              ) : (
                <div className="space-y-1.5">
                  {data.referrals.map((r, i) => (
                    <div key={i} className="text-xs text-slate-300 flex items-center justify-between gap-2">
                      <span className="truncate">{r.referred_email || r.referred_user_id || "—"}</span>
                      <span className="text-slate-500 fl-mono text-[10px] uppercase tracking-widest shrink-0">{r.created_at?.slice(0,10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value, mono }) {
  return (
    <div className="fl-card p-2.5">
      <div className="fl-mono text-[9px] tracking-widest uppercase text-slate-500">{label}</div>
      <div className={`text-slate-200 mt-0.5 truncate ${mono ? "fl-mono text-[11px]" : "text-xs"}`} title={value}>
        {value || "—"}
      </div>
    </div>
  );
}
